//uni_3221
import styled from '@emotion/styled'
import React, { useEffect } from 'react'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import Image from 'next/image'
import fitty from 'fitty'
import { useLocale } from '@/hooks/useLocale'

type Props = {
  targetId: string
  templateColor: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
  uid: string
  name: string
  sex: Sex
  platform: Array<Platform>
  voiceChat: Array<VoiceChat>
  fontFamily: string
  fontColor: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  playTime: string
  weapon: string
  vehicle: string
  playStyle: string
  hitokoto: string
  characters: number[]
}

export const BF2042_1: React.FC<Props> = ({
  targetId,
  name,
  uid,
  sex,
  fontFamily,
  fontColor,
  platform,
  templateColor,
  voiceChat,
  playStyle,
  characters,
  weapon,
  vehicle,
  hitokoto,
}) => {
  const { t } = useLocale()

  useEffect(() => {
    fitty('#name', {
      maxSize: 20,
      minSize: 8,
    })
    fitty('#uid', {
      maxSize: 20,
      minSize: 8,
    })
    fitty('#platform', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#voiceChat', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#playStyle', {
      maxSize: 16,
      minSize: 11,
    })
    fitty('#weapon', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#vehicle', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#hitokoto', {
      maxSize: 24,
      minSize: 12,
    })
  }, [])
  return (
    <Container id={targetId} fontFamily={fontFamily} fontColor={fontColor}>
      <Image
        width="1280"
        height="720"
        priority={true}
        unoptimized={true}
        src={`/static/images/profiles/bf2042_1/${templateColor}.png`}
        alt={`BF2042自己紹介カード ${templateColor}`}
      />
      <Name>
        <span id="name">{name}</span>
      </Name>
      <Id>
        <span id="uid">{uid}</span>
      </Id>
      {sex === 0 && <Sex0 />}
      {sex === 1 && <Sex1 />}
      {sex === 2 && <Sex2 />}
      <PlatformText>
        <span id="platform">
          {platform.includes(1) && ' PlayStation '}
          {platform.includes(3) && ' Switch '}
          {platform.includes(2) && ' Xbox '}
          {platform.includes(0) && ' PC '}
        </span>
      </PlatformText>
      <VCText>
        <span id="voiceChat">
          {voiceChat.includes(0) && ' Skype '}
          {voiceChat.includes(1) && ' LINE '}
          {voiceChat.includes(2) && ' プレステ '}
          {voiceChat.includes(3) && ' Discord '}
          {voiceChat.includes(5) && ' Switch '}
        </span>
      </VCText>
      <PlayStyle>
        <span id="playStyle">{playStyle}</span>
      </PlayStyle>
      {characters.includes(0) && <Character0 />}
      {characters.includes(1) && <Character1 />}
      {characters.includes(2) && <Character2 />}
      {characters.includes(3) && <Character3 />}
      {characters.includes(4) && <Character4 />}
      {characters.includes(5) && <Character5 />}
      {characters.includes(6) && <Character6 />}
      {characters.includes(7) && <Character7 />}
      {characters.includes(8) && <Character8 />}
      {characters.includes(9) && <Character9 />}
      {characters.includes(10) && <Character10 />}
      {characters.includes(11) && <Character11 />}
      {characters.includes(12) && <Character12 />}
      {characters.includes(13) && <Character13 />}
      <Weapon>
        <span id={'weapon'}>{weapon}</span>
      </Weapon>
      <Vehicle>
        <span id={'vehicle'}>{vehicle}</span>
      </Vehicle>
      <Hitokoto>
        <span id={'hitokoto'}>{hitokoto}</span>
      </Hitokoto>
    </Container>
  )
}

const Container = styled.div<{ fontFamily: string; fontColor: string }>`
  position: relative;
  width: 100%;
  line-height: 0;
  * {
    line-height: 1;
    font-family: ${({ fontFamily }) => fontFamily};
    color: ${({ fontColor }) => fontColor};
    border-color: ${({ fontColor }) => fontColor};
  }
`

const Name = styled.div`
  position: absolute;
  top: 17%;
  left: 3%;
  width: 45%;
  height: 14%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
  }
`

const Id = styled.div`
  position: absolute;
  top: 27.5%;
  left: 3%;
  width: 45%;
  height: 14%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
  }
`

const SexBase = styled.div`
  position: absolute;
  top: 14.5%;
  left: 37%;
  border-radius: 50%;
  border-style: solid;
  border-width: 2px;
  width: 16px;
  height: 16px;
`

const Sex0 = styled(SexBase)``
const Sex1 = styled(SexBase)`
  left: 40.8%;
`
const Sex2 = styled(SexBase)`
  left: 44.5%;
`

const PlatformText = styled.div`
  position: absolute;
  top: 37%;
  left: 2%;
  height: 24%;
  width: 46%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    font-weight: bold;
    line-height: 1.4;
  }
`

const VCText = styled.div`
  position: absolute;
  top: 51%;
  left: 2%;
  height: 24%;
  width: 46%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    font-weight: bold;
    line-height: 1.4;
  }
`

const PlayStyle = styled.div`
  position: absolute;
  top: 74%;
  left: 2%;
  height: 24%;
  width: 46%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    font-weight: bold;
    line-height: 1.4;
  }
`

const CharacterBase = styled.div`
  position: absolute;
  top: 7%;
  left: 58.5%;
  border-style: solid;
  border-width: 3px;
  border-radius: 50%;
  width: 32px;
  height: 32px;
`

const Character0 = styled(CharacterBase)``
const Character1 = styled(CharacterBase)`
  left: 67.5%;
`
const Character2 = styled(CharacterBase)`
  left: 76.5%;
`
const Character3 = styled(CharacterBase)`
  left: 85%;
`
const Character4 = styled(CharacterBase)`
  top: 16.5%;
  left: 58.5%;
`

const Character5 = styled(CharacterBase)`
  top: 16.5%;
  left: 67.5%;
`

const Character6 = styled(CharacterBase)`
  top: 16.5%;
  left: 76.5%;
`

const Character7 = styled(CharacterBase)`
  top: 28%;
  left: 58.5%;
`

const Character8 = styled(CharacterBase)`
  top: 28%;
  left: 67.5%;
`

const Character9 = styled(CharacterBase)`
  top: 28%;
  left: 76.5%;
`

const Character10 = styled(CharacterBase)`
  top: 39.5%;
  left: 58.5%;
`

const Character11 = styled(CharacterBase)`
  top: 39.5%;
  left: 67.5%;
`

const Character12 = styled(CharacterBase)`
  top: 39.5%;
  left: 76.5%;
`

const Character13 = styled(CharacterBase)`
  top: 39.5%;
  left: 85%;
`

const Weapon = styled.div`
  position: absolute;
  top: 57%;
  left: 51%;
  width: 21%;
  height: 10%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    font-weight: bold;
    line-height: 1.4;
  }
`

const Vehicle = styled.div`
  position: absolute;
  top: 57%;
  left: 75.5%;
  width: 21%;
  height: 10%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    font-weight: bold;
    line-height: 1.4;
  }
`

const Hitokoto = styled.div`
  position: absolute;
  top: 74%;
  left: 51%;
  height: 24%;
  width: 46%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    font-weight: bold;
    line-height: 1.4;
  }
`
